<script setup lang="ts">
// Define allowed event names explicitly to avoid using `any` in the SFC generic.
const emit = defineEmits([
  'update-games-list',
  'update-pagination',
  'update:filters',
  'update:search',
]);

interface FetchGamesParams {
  moreGames?: boolean;
  showLoading?: boolean; // when false, do not toggle global `isLoading` during fetch
  _searchToken?: number;
}

const route: any = useRoute();

const { isDesktop } = useLayout();
const { getGamesFromCore, getGameFilterOptions } = useCasino();
const { isLoading, isLoadingMoreGames } = storeToRefs(useCasinoStore());

const searchTimeout = ref<any>(null);
const SEARCH_DEBOUNCE_MS = 400;

// token to ignore stale search responses (logical cancel)
let latestSearchToken = 0;

// Coalesce multiple synchronous filter changes into a single fetch.
// When category changes also modify sortBy/sortDir, each watcher would
// fire its own fetchGames call. This flag ensures only one fetch per tick.
let fetchScheduled = false;
let scheduledParams: FetchGamesParams = {};

function scheduleFetch(params: FetchGamesParams = {}) {
  scheduledParams = params;
  if (fetchScheduled) return;
  fetchScheduled = true;
  nextTick(() => {
    fetchScheduled = false;
    fetchGames(scheduledParams);
    saveFiltersToStorage();
  });
}

const MAIS_PAGOS_SLUG = 'mais-pagos';

const FILTERS_STORAGE_KEY = 'casino_filters_state';
const NAVIGATION_FROM_GAME_KEY = 'casino_navigation_from_game';
const ORIGIN_URL_KEY = 'casino_origin_url';

// Helper functions for filter persistence
const saveFiltersToStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    const filtersState = {
      categories: selectedCategories.value,
      providers: selectedProviders.value,
      search: searchQuery.value,
      sortBy: selectedSortBy.value,
      sortDir: selectedSortDir.value,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersState));
  } catch (e) {
    // Ignore storage errors
  }
};

const isNavigatingFromGame = () => {
  if (typeof window === 'undefined') return false;
  try {
    const flag = sessionStorage.getItem(NAVIGATION_FROM_GAME_KEY);

    sessionStorage.removeItem(NAVIGATION_FROM_GAME_KEY);

    return flag === 'true';
  } catch (e) {
    return false;
  }
};

const loadFiltersFromStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);

    if (!stored) return null;

    const filtersState = JSON.parse(stored);
    // Only restore filters if they were saved in the last 30 minutes
    const isRecent = Date.now() - filtersState.timestamp < 30 * 60 * 1000;

    return isRecent ? filtersState : null;
  } catch (e) {
    return null;
  }
};

const clearFiltersFromStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(FILTERS_STORAGE_KEY);
  } catch (e) {
    // Ignore storage errors
  }
};

const selectedCategories = ref<string[]>([]);
const selectedProviders = ref<string[]>([]);
const selectedTypes = ref<string[]>([]);
const selectedSortBy = ref<string>('');
const selectedSortDir = ref<string>('');
const isInitializing = ref<boolean>(true);
const categoriesList = ref<any[]>([]);
const allCategoriesList = ref<any[]>([]); // New ref for all categories
const providersList = ref<any[]>([]);
const allProvidersList = ref<any[]>([]); // New ref for all providers
const typesList = ref<GameFilterType[]>([]);
const sortByList = ref<GameSortOption[]>([]);
const sortDirList = ref<GameSortOption[]>([]);
const noSearchResults = ref<boolean>(false);
const searchQuery = ref<string>('');
const page = ref<number>(1);
const lastFetchFailed = ref<boolean>(false);

const isMaisPagos = computed(() =>
  selectedCategories.value.includes(MAIS_PAGOS_SLUG),
);

const gamesObject = ref<any>({});
// Local cache keyed by current filters (categories+providers+sort)
const gamesCache = ref<Map<string, any[]>>(new Map());

const getFilterKey = () => {
  const cats = [...selectedCategories.value].slice().sort();
  const provs = [...selectedProviders.value].slice().sort();

  return JSON.stringify({
    categories: cats,
    providers: provs,
    sort: sortBy.value,
  });
};

const categorySlug = computed<string>(() => {
  const { category } = route.params;

  if (Array.isArray(category)) {
    const [categorySlug] = category;

    return String(categorySlug).toLowerCase();
  } else if (category) {
    return String(category).toLowerCase();
  }

  return '';
});

const categoriesName = computed<string>(() => {
  const currentCategories = categoriesList.value.filter((category) => {
    const categorySlug = String(category.slug).toLowerCase();

    return selectedCategories.value.some((selectedCategory) => {
      return categorySlug === String(selectedCategory).toLowerCase();
    });
  });

  const currentCategoriesNames = currentCategories.map((selectedCategories) => {
    return selectedCategories.name;
  });

  if (currentCategoriesNames.length) {
    return currentCategoriesNames.join(', ');
  }

  return '';
});

const providerSlug = computed<string>(() => {
  const { provider } = route.params;

  if (provider) {
    return String(provider).toLowerCase();
  }

  return '';
});

const providersName = computed<string>(() => {
  const currentProviders = providersList.value.filter((provider) => {
    const providerSlug = String(provider.slug).toLowerCase();

    return selectedProviders.value.some((selectedProvider) => {
      return providerSlug === String(selectedProvider).toLowerCase();
    });
  });

  const currentProvidersNames = currentProviders.map((selectedProviders) => {
    return selectedProviders.name;
  });

  if (currentProvidersNames.length) {
    return currentProvidersNames.join(', ');
  }

  return '';
});

const sortBy = computed<string>(() => {
  const { category: params } = route.params;

  if (Array.isArray(params) && params.length > 1) {
    const [, sortOrderParam] = params;

    return String(sortOrderParam).toLowerCase();
  }

  return '';
});

function isSlugValid(slug: string): boolean {
  return !!(slug && slug !== 'all');
}

function onFetchGamesHandle(data: any) {
  const paginationData = {
    currentPage: data.current_page,
    from: data.from,
    to: data.to,
    total: data.total,
    lastPage: data.last_page,
  };

  emit('update-pagination', paginationData);

  // Always update categoriesList and providersList with the latest data from the API
  categoriesList.value = data.categories;
  providersList.value = data.providers;

  // Initialize allCategoriesList if it's empty
  if (allCategoriesList.value.length === 0) {
    allCategoriesList.value = data.categories;
  }

  // Initialize allProvidersList if it's empty
  if (allProvidersList.value.length === 0) {
    allProvidersList.value = data.providers;
  }

  // Show no-results warning whenever the API returns no items
  noSearchResults.value = !data.data.length;

  // Only validate if we have loaded the lists from API
  const hasLoadedLists =
    providersList.value.length > 0 || categoriesList.value.length > 0;

  const isProviderValid =
    !providerSlug.value ||
    providersList.value.some((provider) => {
      return String(provider.slug).toLowerCase() === providerSlug.value;
    });

  const isCategoryValid =
    !categorySlug.value ||
    categoriesList.value.some((category) => {
      return String(category.slug).toLowerCase() === categorySlug.value;
    });

  // Allow "recomendados", "todos" and "mais-pagos" as special categories that don't require validation
  const isSpecialCategory =
    categorySlug.value === 'recomendados' ||
    categorySlug.value === 'todos' ||
    categorySlug.value === MAIS_PAGOS_SLUG;

  // Only redirect if lists are loaded and both category and provider are invalid
  if (
    hasLoadedLists &&
    !isProviderValid &&
    !isCategoryValid &&
    !isSpecialCategory &&
    categorySlug.value !== 'all'
  ) {
    navigateTo(localeLink('/'));
  }

  const catSeoName = computed(() => {
    if (
      categorySlug.value === 'all' ||
      categorySlug.value === 'todos' ||
      selectedCategories.value.includes('todos')
    ) {
      return 'Todos os jogos de cassino';
    }

    if (categorySlug.value === 'recomendados') {
      return 'Jogos Recomendados';
    }

    if (selectedCategories.value.includes(MAIS_PAGOS_SLUG)) {
      return 'Jogos Mais Pagou';
    }

    return `Jogos de ${categoriesName.value}`;
  });

  const titleSeo = ref(
    `${catSeoName.value || providersName.value} | ${appName()} | Cassino Online | Casa de Apostas`,
  );

  const description = ref(
    `${catSeoName.value || providersName.value} | ${appName()} | Cassino Online | Casa de Apostas`,
  );

  if (isProviderValid) {
    titleSeo.value = `${providersName.value} 🍀 ${appName()} | Cassino Online | Casa de Apostas`;

    description.value = `Jogue na ${providersName.value} agora na ${appName()} 🎰 Cassino Online | Casa de Apostas`;
  }

  if (
    isCategoryValid ||
    categorySlug.value === 'all' ||
    categorySlug.value === 'todos'
  ) {
    titleSeo.value = `${catSeoName.value} 🍀 ${appName()} | Cassino Online | Casa de Apostas`;

    description.value = `${catSeoName.value} disponíveis para jogar na ${appName()} 🎰 Cassino Online | Casa de Apostas`;
  }

  useSeo({
    title: titleSeo.value,
    description: description.value,
  });
}

function fetchGames({
  moreGames = false,
  showLoading = true,
  _searchToken,
}: FetchGamesParams = {}) {
  if (moreGames) {
    page.value++;
  }

  // Block loading beyond page 2 for "Mais Pagos"
  if (isMaisPagos.value && page.value > 2) {
    page.value = 2;

    return Promise.resolve();
  }

  page.value = moreGames ? page.value : 1;

  // For "Mais Pagos" and "Todos", don't send categories (virtual categories)
  const categoriesToSend =
    isMaisPagos.value || selectedCategories.value.includes('todos')
      ? []
      : selectedCategories.value.map((e: any) => ['categories[]', e]);

  const filterParams = [
    ...categoriesToSend,
    ...selectedProviders.value.map((e: any) => ['providers[]', e]),
    ...selectedTypes.value.map((e: any) => ['types[]', e]),
    ['sort', sortBy.value],
    ['per_page', route.query.per_page || '30'],
    ['page', page.value],
    ['term', searchQuery.value || ''],
  ];

  // For "Mais Pagos", force sort_by=pay_today and sort_dir=desc
  if (isMaisPagos.value) {
    filterParams.push(['sort_by', 'pay_today']);
    filterParams.push(['sort_dir', 'desc']);
  } else if (searchQuery.value) {
    // When searching, always sort by pay_today desc
    filterParams.push(['sort_by', 'pay_today']);
    filterParams.push(['sort_dir', 'desc']);
  } else {
    // Add new filter parameters only if they have values (not "sem filtro")
    if (selectedSortBy.value && selectedSortBy.value !== '') {
      filterParams.push(['sort_by', selectedSortBy.value]);
    }
    if (selectedSortDir.value && selectedSortDir.value !== '') {
      filterParams.push(['sort_dir', selectedSortDir.value]);
    }
  }

  if (!moreGames) {
    if (showLoading) isLoading.value = true;
    else emit('update:search', true);
  } else {
    isLoadingMoreGames.value = true;
  }

  return getGamesFromCore(filterParams)
    .then((response) => {
      // Ignore stale search responses when a newer search was started
      if (typeof _searchToken !== 'undefined' && _searchToken !== null) {
        if (_searchToken !== latestSearchToken) {
          // stale response, ignore
          return;
        }
      }

      // Cache only when fetching the first page and there is no search term
      const key = getFilterKey();

      if (page.value === 1 && !searchQuery.value) {
        try {
          gamesCache.value.set(key, response.data);
        } catch (e) {
          // noop
        }
      }

      const previousData = Array.isArray(gamesObject.value?.data)
        ? gamesObject.value.data
        : [];

      const newData = moreGames
        ? [...previousData, ...response.data]
        : response.data;

      gamesObject.value = { ...response, data: newData };

      emit('update-games-list', gamesObject.value);

      onFetchGamesHandle(response);

      lastFetchFailed.value = false;
    })
    .catch((error) => {
      console.error(error);

      // Revert the page increment so the next attempt retries the same page
      // instead of skipping ahead.
      if (moreGames && page.value > 1) {
        page.value--;
      }

      // Preserve already-loaded games on moreGames failure (don't wipe to []).
      if (!moreGames) {
        gamesObject.value = { ...gamesObject.value, data: [] };

        // Emit empty list so the parent can dismiss the skeleton overlay,
        // and surface the "no results" feedback as the failure UI.
        emit('update-games-list', { ...gamesObject.value, data: [] });
        noSearchResults.value = true;
      }

      lastFetchFailed.value = true;
    })
    .finally(() => {
      if (showLoading) {
        isLoading.value = false;
      } else if (
        typeof _searchToken === 'undefined' ||
        _searchToken === latestSearchToken
      ) {
        // only clear deferred search signal if this response is the latest search
        emit('update:search', false);
      }

      isLoadingMoreGames.value = false;
    });
}

// Prefetch a larger page into cache for smoother client-side searching
async function prefetchCache(prefetchCount = 200) {
  try {
    const key = getFilterKey();

    if (gamesCache.value.has(key)) return;

    const categoriesToPrefetch = selectedCategories.value.includes('todos')
      ? []
      : selectedCategories.value.map((e: any) => ['categories[]', e]);

    const filterParams = [
      ...categoriesToPrefetch,
      ...selectedProviders.value.map((e: any) => ['providers[]', e]),
      ['sort', sortBy.value],
      ['per_page', String(prefetchCount)],
      ['page', 1],
      ['term', ''],
    ];

    const response = await getGamesFromCore(filterParams, { silent: true });

    if (Array.isArray(response?.data)) {
      gamesCache.value.set(key, response.data);
    }
  } catch (e) {
    // noop — prefetch is best-effort
  }
}

watch(searchQuery, () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  const keyForCache = getFilterKey();
  const cached = gamesCache.value.get(keyForCache);

  // Only use cache for client-side filtering if there's NO search term
  // When searching, always hit the API to get all results across all pages
  const shouldUseCache = cached && !searchQuery.value;

  const delay = shouldUseCache ? 0 : SEARCH_DEBOUNCE_MS;

  searchTimeout.value = setTimeout(() => {
    // Attempt client-side filtering using cache first (only when NOT searching)
    if (shouldUseCache && Array.isArray(cached)) {
      const term = String(searchQuery.value || '')
        .trim()
        .toLowerCase();

      const filtered = term
        ? cached.filter((g: any) => {
            const name = String(g.name || '').toLowerCase();
            const slug = String(g.slug || '').toLowerCase();

            return name.includes(term) || slug.includes(term);
          })
        : cached.slice();

      const fakeResponse = {
        data: filtered,
        current_page: 1,
        from: filtered.length ? 1 : 0,
        to: filtered.length,
        total: filtered.length,
        last_page: 1,
        categories: categoriesList.value,
        providers: providersList.value,
      };

      // Emit the filtered results without toggling global loading
      gamesObject.value = { ...gamesObject.value, data: filtered };
      emit('update-games-list', fakeResponse);
      onFetchGamesHandle(fakeResponse);

      emit('update:search', false);

      return;
    }

    // Fallback: request server (no global skeleton while typing)
    // mark a new search token and capture it so we can ignore stale responses
    const thisSearchToken = ++latestSearchToken;

    fetchGames({ showLoading: false, _searchToken: thisSearchToken });
  }, delay);
});

watch(
  () => selectedCategories.value,
  (newVal, oldVal) => {
    if (!isInitializing.value) {
      const enteringMaisPagos =
        newVal.includes(MAIS_PAGOS_SLUG) &&
        (!oldVal || !oldVal.includes(MAIS_PAGOS_SLUG));

      const leavingMaisPagos =
        oldVal &&
        oldVal.includes(MAIS_PAGOS_SLUG) &&
        !newVal.includes(MAIS_PAGOS_SLUG);

      const enteringTodos =
        newVal.includes('todos') && (!oldVal || !oldVal.includes('todos'));

      const leavingTodos =
        oldVal && oldVal.includes('todos') && !newVal.includes('todos');

      if (enteringMaisPagos) {
        // Force sort params when entering "Mais Pagos"
        selectedSortBy.value = 'pay_today';
        selectedSortDir.value = 'desc';
      } else if (enteringTodos) {
        // Pagou hoje desc como padrão para "Todos"
        selectedSortBy.value = 'pay_today';
        selectedSortDir.value = 'desc';
      } else if (leavingMaisPagos || leavingTodos) {
        // Voltando para categoria normal — ordem padrão do backoffice
        selectedSortBy.value = '';
        selectedSortDir.value = 'asc';
      }

      // Use scheduleFetch to coalesce with sort param changes above
      scheduleFetch({ showLoading: false });
    }
  },
  { deep: true },
);

watch(
  () => selectedProviders.value,
  () => {
    if (!isInitializing.value) {
      scheduleFetch({ showLoading: false });
    }
  },
  { deep: true },
);

watch(
  () => selectedTypes.value,
  () => {
    if (!isInitializing.value) {
      scheduleFetch({ showLoading: false });
    }
  },
  { deep: true },
);

watch(
  () => selectedSortBy.value,
  (newVal) => {
    if (!isInitializing.value) {
      if (newVal === 'pay_today') {
        selectedSortDir.value = 'desc';
      } else {
        selectedSortDir.value = 'asc';
      }

      scheduleFetch({ showLoading: false });
    }
  },
);

watch(
  () => selectedSortDir.value,
  () => {
    if (!isInitializing.value) {
      scheduleFetch({ showLoading: false });
    }
  },
);

watch(searchQuery, () => {
  if (!isInitializing.value) {
    saveFiltersToStorage();
  }
});

watch(
  () => route,
  (to) => {
    if (to.name === 'games') {
      categoriesList.value = [];
      providersList.value = [];
      isLoading.value = true;
      // Clear filters when navigating to a different games page
      clearFiltersFromStorage();
    }
  },
);

// Reage a mudanças no query param initialCategory (ex: sidebar muda de roletas para jogos-ao-vivo)
watch(
  () => route.query.initialCategory,
  async (newCategory) => {
    if (isInitializing.value) return;

    if (newCategory) {
      selectedCategories.value = [newCategory as string];
    } else if (categorySlug.value === 'all') {
      // Query param removido e estamos em /games/category/all → resetar para "todos"
      selectedCategories.value = ['todos'];
    }

    await fetchGames();
    saveFiltersToStorage();
  },
);

// Clear filters when explicitly navigating away from the games category page
onBeforeUnmount(() => {
  // Save the current URL as the origin for back navigation
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(ORIGIN_URL_KEY, route.fullPath);
    } catch (e) {
      // Ignore storage errors
    }
  }

  // Only clear if we're not on a game detail page (preserve filters for back navigation)
  if (!route.path.includes('/games/')) {
    clearFiltersFromStorage();
  }

  // Always clear the navigation flag when leaving this page
  // It will be set again only when entering a game page
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(NAVIGATION_FROM_GAME_KEY);
    } catch (e) {
      // Ignore
    }
  }
});

defineExpose({
  fetchGames,
  selectedCategories,
  allCategoriesList,
  isMaisPagos,
  lastFetchFailed,
});

onMounted(async () => {
  // Fetch filter options from API
  try {
    const filterOptions = await getGameFilterOptions();

    if (filterOptions) {
      // Set categories from filter options endpoint
      if (filterOptions.categories && Array.isArray(filterOptions.categories)) {
        allCategoriesList.value = filterOptions.categories;
        categoriesList.value = filterOptions.categories;
      }

      // Set providers from filter options endpoint
      if (filterOptions.providers && Array.isArray(filterOptions.providers)) {
        allProvidersList.value = filterOptions.providers;
        providersList.value = filterOptions.providers;
      }

      typesList.value = filterOptions.types || [];
      sortByList.value = filterOptions.sort_by || [];
      sortDirList.value = filterOptions.sort_dir || [];

      // Default sort: sem filtro (ordem padrão do backoffice)
      // Sort só é forçado ao selecionar "Mais Pagos"
    }
  } catch (error) {
    console.error(
      '[ApostaTudoHeader] Erro ao buscar opções de filtros:',
      error,
    );
  }

  // Check if we're coming back from a game page
  const comingFromGame = isNavigatingFromGame();
  const shouldRestoreFilters = comingFromGame;

  // Try to restore filters from storage if flag is set
  const savedFilters = shouldRestoreFilters ? loadFiltersFromStorage() : null;

  // Clear the flag after checking it
  if (comingFromGame && typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(NAVIGATION_FROM_GAME_KEY);
    } catch (e) {
      // Ignore
    }
  }

  // Check for query params to initialize filters
  const initialCategory = route.query.initialCategory as string | undefined;
  const initialProvider = route.query.initialProvider as string | undefined;

  // Determine if we have initial filters from route or query params
  const hasInitialCategory =
    isSlugValid(categorySlug.value) || !!initialCategory;

  const hasInitialProvider =
    isSlugValid(providerSlug.value) || !!initialProvider;

  const hasInitialFilters = hasInitialCategory || hasInitialProvider;

  // Priority: saved filters when coming from valid page > initial filters from route/query
  if (savedFilters && shouldRestoreFilters) {
    // Coming back from game - restore saved filters (highest priority)
    // Categorias/providers ja vieram de getGameFilterOptions acima — pulamos o
    // primeiro fetchGames sem filtros e vamos direto para o fetch com os
    // filtros restaurados.

    // Restore saved filters
    if (savedFilters.categories?.length) {
      selectedCategories.value = savedFilters.categories;
    }
    if (savedFilters.providers?.length) {
      selectedProviders.value = savedFilters.providers;
    }
    if (savedFilters.search) {
      searchQuery.value = savedFilters.search;
    }
    if (savedFilters.sortBy) {
      selectedSortBy.value = savedFilters.sortBy;
    }
    // Se sortBy está vazio, forçar pagou hoje desc como padrão
    if (savedFilters.sortBy) {
      selectedSortDir.value = savedFilters.sortDir || 'desc';
    } else {
      selectedSortBy.value = 'pay_today';
      selectedSortDir.value = 'desc';
    }

    // Fetch with restored filters applied
    await fetchGames();
  } else if (hasInitialFilters) {
    // First time or normal navigation - use route/query params.
    // Categorias/providers ja vieram de getGameFilterOptions acima.

    // Now set the filters
    if (categorySlug.value === 'all' && initialCategory) {
      // Use query param when available (e.g. ?initialCategory=mais-pagos)
      selectedCategories.value = [initialCategory];
    } else if (categorySlug.value === 'all') {
      // Map route parameter 'all' to category slug 'todos'
      selectedCategories.value = ['todos'];
    } else if (isSlugValid(categorySlug.value)) {
      selectedCategories.value = [categorySlug.value];
    } else if (initialCategory) {
      selectedCategories.value = [initialCategory];
    }

    if (isSlugValid(providerSlug.value)) {
      selectedProviders.value = [providerSlug.value];
    } else if (initialProvider) {
      selectedProviders.value = [initialProvider];
    }

    // Set default sort based on selected category
    const selectedCat = selectedCategories.value[0];

    if (selectedCat === 'todos' || selectedCat === MAIS_PAGOS_SLUG) {
      selectedSortBy.value = 'pay_today';
      selectedSortDir.value = 'desc';
    } else {
      selectedSortBy.value = '';
      selectedSortDir.value = 'asc';
    }

    // Fetch games again with filters applied
    await fetchGames();

    // Wait for DOM updates before saving filters
    await nextTick();

    // Save the initial filters so they can be restored when coming back
    saveFiltersToStorage();
  } else {
    // No initial filters - select 'todos' as default
    selectedCategories.value = ['todos'];
    selectedSortBy.value = 'pay_today';
    selectedSortDir.value = 'desc';

    // Fetch games with 'todos' selected
    await fetchGames();

    // Save current state
    await nextTick();
    saveFiltersToStorage();
  }

  // Mark initialization as complete
  await nextTick();
  isInitializing.value = false;

  // Attempt to prefetch a larger batch for client-side filtering (no UI blocking)
  prefetchCache(200);
});
</script>

<template>
  <SectionsApostaTudoGamesFilter
    v-model:search-query="searchQuery"
    v-model:selected-categories="selectedCategories"
    v-model:selected-providers="selectedProviders"
    v-model:selected-sort-by="selectedSortBy"
    v-model:selected-sort-dir="selectedSortDir"
    v-model:selected-types="selectedTypes"
    :categories-list="allCategoriesList"
    :is-filters-active="true"
    :is-mobile="!isDesktop"
    :is-search-active="true"
    :is-sort-locked="isMaisPagos"
    :providers-list="allProvidersList"
    :sort-by-list="sortByList"
    :sort-dir-list="sortDirList"
    :types-list="typesList"
  />
  <div
    v-if="noSearchResults && !isLoading"
    class="alert alert-warning"
  >
    <span> Não encontramos resultados para sua busca </span>
  </div>
</template>
